import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatUtcDate } from '../../utils/dateTime';

export interface StateReferenceItem {
  label: string;
  value: string;
}

interface StateReferenceAccordionProps {
  items: StateReferenceItem[];
  /** Revision date of the state reference data, shown in the summary row. */
  revisionDate?: string | null;
}

/** Read-only accordion of state requirement reference text (credit limitations, policies, etc). */
export default function StateReferenceAccordion({ items, revisionDate }: StateReferenceAccordionProps) {
  if (items.length === 0) return null;

  return (
    <Accordion
      elevation={0}
      disableGutters
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        '&::before': { display: 'none' },
        '&:first-of-type, &:last-of-type': { borderRadius: 1 },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            columnGap: 1.5,
            flexGrow: 1,
            mr: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            State Requirements Reference (read-only)
          </Typography>
          {revisionDate && (
            <Typography variant="caption" color="text.secondary">
              Last updated {formatUtcDate(revisionDate)}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {items.map((item) => (
            <Box key={item.label}>
              <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1.5, display: 'block' }}>
                {item.label}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
